import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Download, UploadCloud, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";

const CATEGORIES = [
  { key: "employees", label: "Employees", headers: "Name, Employee Code, Email, Phone, Pay Rate, Active", keyField: "Employee Code" },
  { key: "jobcodes", label: "Job Codes", headers: "Category, Category Code, Job, Job Code", keyField: "Job Code" },
  { key: "equipment", label: "Equipment", headers: "Name, Code, Category, Active", keyField: "Name" },
  { key: "ranches", label: "Ranches", headers: "Ranch Number, Ranch Name, Block, Address, City, County, State, ZIP, Acres, APN", keyField: "Ranch Number + Block" },
];

function ImportCard({ cat }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/import/${cat.key}/template`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cat.key}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not download template.");
    }
  };

  // Step 1: ask the server what would happen (no writes) and show a confirmation.
  const startPreview = async () => {
    if (!file) return;
    setBusy(true); setResult(null); setError(null); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${cat.key}?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // Step 2: confirmed — apply for real.
  const confirmImport = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import/${cat.key}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setPreview(null);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 p-5 shadow-sm shadow-stone-900/5" data-testid={`import-card-${cat.key}`}>
      <div className="flex items-center gap-2 mb-1">
        <FileSpreadsheet size={20} className="text-[#1F3D34]" />
        <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{cat.label}</h2>
      </div>
      <p className="text-xs text-stone-500 mb-1">Columns: <span className="font-mono">{cat.headers}</span></p>
      <p className="text-[11px] text-stone-400 mb-4">Matched by <span className="font-semibold">{cat.keyField}</span>. Rows not in your file are marked inactive (kept for history).</p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          data-testid={`download-template-${cat.key}`}
          onClick={downloadTemplate}
          className="h-11 px-4 bg-white text-[#1F3D34] font-bold uppercase text-xs border border-stone-200 flex items-center justify-center gap-2 hover:bg-[#D8C7A1]"
        >
          <Download size={15} /> Template
        </button>

        <input
          ref={inputRef}
          data-testid={`file-input-${cat.key}`}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setError(null); setPreview(null); }}
          className="flex-1 text-sm file:mr-3 file:h-11 file:px-4 file:border-0 file:bg-stone-100 file:font-bold file:uppercase file:text-xs border border-stone-200 h-11"
        />

        <button
          data-testid={`upload-btn-${cat.key}`}
          onClick={startPreview}
          disabled={!file || busy}
          className="h-11 px-5 bg-[#1F3D34] text-white font-bold uppercase text-xs border border-stone-200 flex items-center justify-center gap-2 disabled:opacity-40"
        >
          <UploadCloud size={15} /> {busy ? "Checking…" : "Review & Update"}
        </button>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-300 text-red-700 text-sm flex items-start gap-2" data-testid={`import-error-${cat.key}`}>
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-3 p-3 bg-[#7C9673]/15 border border-[#7C9673] text-sm" data-testid={`import-result-${cat.key}`}>
          <div className="flex items-center gap-2 font-semibold text-[#1F3D34]">
            <CheckCircle2 size={16} /> Import complete
          </div>
          <div className="mt-1 font-mono text-xs text-stone-700">
            {result.created} created · {result.updated} updated · {result.deactivated} deactivated
          </div>
          {result.errors?.length > 0 && (
            <ul className="mt-2 text-xs text-red-700 list-disc pl-5" data-testid={`import-row-errors-${cat.key}`}>
              {result.errors.slice(0, 20).map((er, i) => (<li key={i}>{er}</li>))}
              {result.errors.length > 20 && <li>…and {result.errors.length - 20} more</li>}
            </ul>
          )}
        </div>
      )}

      {preview && (
        <ConfirmImportModal
          cat={cat}
          preview={preview}
          busy={busy}
          onCancel={() => setPreview(null)}
          onConfirm={confirmImport}
        />
      )}
    </div>
  );
}

function ConfirmImportModal({ cat, preview, busy, onCancel, onConfirm }) {
  const deact = preview.deactivate_list || [];
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      data-testid={`import-confirm-modal-${cat.key}`}
    >
      <div className="bg-white w-full sm:max-w-lg sm:rounded-lg shadow-xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-stone-200">
          <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
            Confirm {cat.label} Import
          </h3>
          <p className="text-sm text-stone-500 mt-1">Review the changes before applying. Nothing has been saved yet.</p>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-3 bg-stone-50 border border-stone-200">
              <div className="text-2xl font-bold text-[#1F3D34]" data-testid={`preview-created-${cat.key}`}>{preview.created}</div>
              <div className="text-[11px] uppercase tracking-wide text-stone-500">Added</div>
            </div>
            <div className="p-3 bg-stone-50 border border-stone-200">
              <div className="text-2xl font-bold text-[#1F3D34]" data-testid={`preview-updated-${cat.key}`}>{preview.updated}</div>
              <div className="text-[11px] uppercase tracking-wide text-stone-500">Updated</div>
            </div>
            <div className={`p-3 border ${deact.length ? "bg-red-50 border-red-300" : "bg-stone-50 border-stone-200"}`}>
              <div className={`text-2xl font-bold ${deact.length ? "text-red-700" : "text-[#1F3D34]"}`} data-testid={`preview-deactivated-${cat.key}`}>{preview.deactivated}</div>
              <div className="text-[11px] uppercase tracking-wide text-stone-500">Deactivated</div>
            </div>
          </div>

          {deact.length > 0 && (
            <div className="border border-red-300 bg-red-50/50" data-testid={`preview-deactivate-list-${cat.key}`}>
              <div className="flex items-center gap-2 p-3 text-sm font-semibold text-red-700 border-b border-red-200">
                <AlertTriangle size={16} />
                These {deact.length} not on your file will be deactivated
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-red-100">
                {deact.map((d) => (
                  <div key={d.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="font-medium text-stone-800">{d.label || d.code || "—"}</span>
                    {d.code && d.code !== d.label && <span className="font-mono text-xs text-stone-500">{d.code}</span>}
                  </div>
                ))}
              </div>
              <p className="px-3 py-2 text-[11px] text-stone-500">
                Deactivated records are hidden from active lists but kept for history (timecards & payroll).
              </p>
            </div>
          )}

          {preview.errors?.length > 0 && (
            <ul className="text-xs text-amber-700 bg-amber-50 border border-amber-300 p-3 list-disc pl-6" data-testid={`preview-errors-${cat.key}`}>
              {preview.errors.slice(0, 10).map((er, i) => (<li key={i}>{er}</li>))}
              {preview.errors.length > 10 && <li>…and {preview.errors.length - 10} more</li>}
            </ul>
          )}
        </div>

        <div className="p-5 border-t border-stone-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            data-testid={`import-cancel-btn-${cat.key}`}
            onClick={onCancel}
            disabled={busy}
            className="h-11 px-5 bg-white text-stone-700 font-bold uppercase text-xs border border-stone-300 hover:bg-stone-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            data-testid={`import-confirm-btn-${cat.key}`}
            onClick={onConfirm}
            disabled={busy}
            className="h-11 px-5 bg-[#1F3D34] text-white font-bold uppercase text-xs flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <CheckCircle2 size={15} /> {busy ? "Applying…" : "Confirm & Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DataImport() {
  return (
    <div className="space-y-6" data-testid="data-import-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Data Import</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">
          Upload an Excel (.xlsx) list to update each category. Download a template to get the exact columns.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {CATEGORIES.map((cat) => (<ImportCard key={cat.key} cat={cat} />))}
      </div>
    </div>
  );
}
